import { useState } from "react";
import { Link } from "react-router";
import { DailyBars, RangePicker } from "../components/charts";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  TableSkeleton,
  PageHeader,
  Table,
  Td,
  Th,
  cx,
} from "../components/ui";
import { formatDateTime, formatNumber, formatRelative } from "../lib/format";
import { useDeployFeed, useDeploySeries } from "../lib/queries";

const LIMIT = 50;

export function DeploymentsPage() {
  const [days, setDays] = useState(30);
  const [offset, setOffset] = useState(0);
  const { data, isPending, error } = useDeployFeed({ limit: LIMIT, offset });
  const series = useDeploySeries(days);

  return (
    <>
      <PageHeader
        title="Deployments"
        description="Requested through Maxlayer, newest first. Git-push builds are not listed."
      />

      <Card>
        <CardHeader
          title="Deploy volume"
          description={
            series.data
              ? `${series.data.total} deploys requested in the last ${days} days`
              : "Deploys requested per day."
          }
          action={
            series.data && <RangePicker ranges={series.data.ranges} value={days} onChange={setDays} />
          }
        />
        {series.isPending ? (
          <LoadingBlock />
        ) : series.error ? (
          <div className="px-4 pb-4">
            <Alert>{series.error.message}</Alert>
          </div>
        ) : (
          <div
            className={cx(
              "px-4 pb-4",
              series.isFetching && "opacity-50 transition-opacity duration-200",
            )}
          >
            <DailyBars
              points={series.data.days.map((day) => ({ day: day.day, value: day.deploys }))}
              format={(value) => formatNumber(value)}
              label="Daily deploy requests"
              windowLabel={`over the last ${days} days`}
            />
            <p className="mt-2 text-[11px] leading-5 text-fg-subtle">
              Only deploys requested through Maxlayer. Ones the engine starts itself, like a git
              push, are not counted.
            </p>
          </div>
        )}
      </Card>

      <Card>
        {isPending ? (
          <TableSkeleton columns={6} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState title="No deployments yet" />
        ) : (
          <>
            {/*
              No app-state column, for the reason spelled out in AppsPage: the
              stored value is the last one we asked for, not the one the engine
              reports. A row here is what was requested and by whom — the app's
              current state is a different question, answered on its detail page.
            */}
            <Table
              head={
                <tr>
                  <Th>When</Th>
                  <Th>App</Th>
                  <Th priority="sm">Organization</Th>
                  <Th priority="lg">Server</Th>
                  <Th priority="md">Kind</Th>
                  <Th priority="lg">Triggered by</Th>
                </tr>
              }
            >
              {data.data.map((entry) => (
                <tr key={entry.id} className="hover:bg-raised">
                  <Td title={formatDateTime(entry.createdAt)} className="whitespace-nowrap">
                    {formatRelative(entry.createdAt)}
                  </Td>
                  <Td>
                    <Link
                      to={`/apps/${entry.app.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {entry.app.name}
                    </Link>
                    {entry.title && (
                      <p className="max-w-xs truncate text-xs text-fg-subtle">
                        {entry.title}
                      </p>
                    )}
                    {/* The organization and server columns drop on a phone, but
                        a deployment without its tenant context is not useful. */}
                    <p className="mt-0.5 text-xs text-fg-subtle sm:hidden">
                      {entry.organization.name}
                      {entry.server && <span className="text-fg-subtle"> · {entry.server}</span>}
                    </p>
                  </Td>
                  <Td priority="sm">
                    <Link to={`/organizations/${entry.organization.id}`} className="hover:underline">
                      {entry.organization.name}
                    </Link>
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">{entry.server ?? "—"}</Td>
                  <Td className="capitalize" priority="md">{entry.kind}</Td>
                  <Td className="text-fg-subtle" priority="lg">{entry.actor ?? "—"}</Td>
                </tr>
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
